import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./JokeBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * JokeBlockComponent — A leaf component for rendering a joke bubble in the chat.
 *
 * It renders the following structure:
 * <div class="chat-message__bubble">
 *   <span class="chat-message__text"></span>
 * </div>
 *
 * Attributes:
 * - block: The JSON stringified joke block object.
 */
export class JokeBlockComponent extends HTMLElement {
    private textSpan: HTMLSpanElement | null = null;

    /**
     * Specifies which attributes should be monitored for changes.
     */
    static get observedAttributes(): string[] {
        return ["block", "role"];
    }

    constructor() {
        super();
    }

    /**
     * Returns the primary text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        return this.getJokeText();
    }

    /**
     * Helper to extract joke text from the block attribute.
     * Aligned with Joke interface in code/functions/jonas_agent/src/types.ts
     */
    private getJokeText(): string {
        const blockAttr = this.getAttribute("block");
        if (!blockAttr) {
            return "";
        }
        try {
            const data = JSON.parse(blockAttr) as Record<string, unknown>;
            return typeof data.joke === "string" ? data.joke : "";
        } catch {
            return "";
        }
    }

    /**
     * Called when the element is first connected to the document's DOM.
     * Initializes the internal structure if not already present.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
        this.updateJoke();
        this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
    }

    /**
     * Called when one of the observed attributes changes.
     * @param name The name of the attribute that changed.
     * @param _oldValue The previous value of the attribute (unused).
     * @param _newValue The new value of the attribute (unused).
     */
    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "block") {
            this.updateJoke();
            this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
        }
    }

    /**
     * Creates the internal DOM structure of the component.
     * Uses standard CSS classes for styling compatibility.
     */
    private render(): void {
        const bubble = document.createElement("div");
        bubble.className = "chat-message__bubble";

        this.textSpan = document.createElement("span");
        this.textSpan.className = "chat-message__text";

        bubble.appendChild(this.textSpan);
        this.shadowRoot?.appendChild(bubble);
    }

    /**
     * Updates the text content of the internal span with the joke.
     * Uses textContent to prevent XSS.
     */
    private updateJoke(): void {
        if (this.textSpan) {
            this.textSpan.textContent = this.getJokeText();
        }
    }
}

/**
 * Register the custom element if it hasn't been registered yet.
 */
if (!customElements.get("jt-joke-block")) {
    customElements.define("jt-joke-block", JokeBlockComponent);
}
